import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref as dbRef, get, remove } from 'firebase/database';
import { ref as storageRef, listAll, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { buildQuotePDF } from './quotePDF';
import { resolveTotal } from './billPDF';
import ConfirmDialog from './ConfirmDialog';
import { ArrowLeft, Download, RefreshCw, FileX, Trash2 } from 'lucide-react';
import './admin-page.css';

function fmtINR(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

export default function QuoteDetails() {
  const { quoteNumber } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regening, setRegening] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    get(dbRef(database, `quote/${quoteNumber}`))
      .then(snap => {
        if (snap.exists()) setQuote(snap.val());
        else setError('Quotation not found.');
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [quoteNumber]);

  const tryDownload = async () => {
    const folderRef = storageRef(storage, `quotes/${quoteNumber}`);
    const result = await listAll(folderRef);
    if (result.items.length === 0) throw new Error('NO_PDF');
    const url = await getDownloadURL(result.items[0]);
    window.open(url, '_blank');
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      await tryDownload();
    } catch (err) {
      if (err.message === 'NO_PDF') setShowRegenDialog(true);
      else setError('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      try {
        const result = await listAll(storageRef(storage, `quotes/${quoteNumber}`));
        await Promise.all(result.items.map(item => deleteObject(item)));
      } catch {}
      await remove(dbRef(database, `quote/${quoteNumber}`));
      navigate('/admin/dashboard/view-quotes');
    } catch (err) {
      setError('Delete failed: ' + err.message);
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRegenerate = async () => {
    if (!quote) return;
    setRegening(true);
    setError('');
    try {
      const total = resolveTotal(quote);
      const pdfBlob = await buildQuotePDF(
        quoteNumber,
        quote.customerName,
        quote.phoneNumber,
        quote.dateOfPurchase,
        quote.items || [],
        total,
        quote.address || ''
      );
      const filename = `${(quote.customerName || 'quote').replace(/\s+/g, '_')}_${(quote.dateOfPurchase || '').replace(/\//g, '-')}_${Date.now()}.pdf`;
      await uploadBytes(
        storageRef(storage, `quotes/${quoteNumber}/${filename}`),
        pdfBlob,
        { contentType: 'application/pdf' }
      );
      setShowRegenDialog(false);
      try { await tryDownload(); } catch {}
    } catch (err) {
      setError('Regeneration failed: ' + err.message);
    } finally {
      setRegening(false);
    }
  };

  if (loading) return <div className="ap-loading">Loading…</div>;

  const total = quote ? resolveTotal(quote) : 0;

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard/view-quotes')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="ap-title">Quotation #{quoteNumber}</h1>
          <p className="ap-subtitle" style={{ wordBreak: 'break-word' }}>{quote?.customerName}</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      {quote && (
        <div className="ap-card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 22 }}>
            <div>
              <div className="bill-detail-label">Customer</div>
              <div className="bill-detail-value">{quote.customerName}</div>
            </div>
            <div>
              <div className="bill-detail-label">Phone</div>
              <div className="bill-detail-value">{quote.phoneNumber}</div>
            </div>
            <div>
              <div className="bill-detail-label">Date</div>
              <div className="bill-detail-value">{quote.dateOfPurchase}</div>
            </div>
            <div>
              <div className="bill-detail-label">Quote #</div>
              <div className="bill-detail-value">{quoteNumber}</div>
            </div>
            {quote.address && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="bill-detail-label">Address</div>
                <div className="bill-detail-value">{quote.address}</div>
              </div>
            )}
          </div>

          <p className="ap-section-title">Items</p>
          <div className="bill-table-wrap">
            <table className="bill-table">
              <thead>
                <tr>
                  {['Item', 'Qty', 'Price', 'Amount', 'Warranty'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((item, i) => (
                  <tr key={i}>
                    <td>{item.itemName}</td>
                    <td>{item.quantity}</td>
                    <td>{fmtINR(item.price)}</td>
                    <td style={{ fontWeight: 500 }}>{fmtINR(Number(item.quantity) * Number(item.price))}</td>
                    <td style={{ color: (!item.warranty || item.warranty === 'No') ? '#9ca3af' : '#059669' }}>
                      {item.warranty || 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bill-total-bar" style={{ marginTop: 14 }}>
            <span>Total Amount</span>
            <span className="bill-total-amount">{fmtINR(total)}</span>
          </div>

          <div className="ap-btn-row">
            <button className="ap-btn ap-btn-primary" onClick={handleDownload} disabled={downloading || regening} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Download size={16} />
              {downloading ? 'Opening…' : 'Download PDF'}
            </button>
            <button
              className="ap-btn ap-btn-danger"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting || downloading || regening}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Trash2 size={15} />
              {deleting ? 'Deleting…' : 'Delete Quote'}
            </button>
          </div>
        </div>
      )}

      {showRegenDialog && (
        <div className="regen-overlay">
          <div className="regen-dialog">
            <div className="regen-dialog-icon"><FileX size={44} strokeWidth={1.2} color="#f59e0b" /></div>
            <h3>Quotation PDF Not Found</h3>
            <p>The PDF for quotation #{quoteNumber} is missing from storage. Would you like to regenerate it from the saved data?</p>
            <div className="regen-dialog-btns">
              <button
                className="ap-btn ap-btn-primary"
                onClick={handleRegenerate}
                disabled={regening}
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}
              >
                <RefreshCw size={15} />
                {regening ? 'Regenerating…' : 'Yes, Regenerate'}
              </button>
              <button className="ap-btn ap-btn-ghost" onClick={() => setShowRegenDialog(false)} disabled={regening}>
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          title="Delete this quotation?"
          message={`Quotation #${quoteNumber} for "${quote?.customerName}" will be permanently deleted. This cannot be undone.`}
          confirmLabel="Yes, Delete"
          confirmingLabel="Deleting…"
          confirming={deleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}
    </div>
  );
}
